import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Briefcase, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { upsertUserAttributes } from "@/lib/userAttributes";
import { toast } from "@/hooks/use-toast";
import { dgTrack } from "@/lib/dataGraph";

/**
 * Just-in-time ask surfaced on the Stack results screen when we don't yet know
 * the member's employer. Saves to user_attributes in place and — if the parent
 * page provides `onEmployerAdded` — re-runs the Stack with the new value so the
 * 4th (employer) layer can populate on the same screen.
 *
 * Members kept typing non-employers here ("retired", "ssdi"), which produced
 * empty employer layers, so obvious non-employers are rejected with a hint and
 * an explicit "not employed" opt-out dismisses the ask instead of saving junk.
 */

const JUNK_EMPLOYERS = new Set([
  "retired", "none", "n/a", "na", "no", "n", "nope", "nothing", "me",
  "ssdi", "ssi", "social security", "disability", "disabled",
  "self", "self employed", "self-employed", "unemployed", "not employed",
  "unknown", "idk", "i don't know", "dont know", "don't know",
  "stay at home", "stay-at-home", "homemaker", "student", "retiree",
]);
const isJunkEmployer = (value: string) =>
  JUNK_EMPLOYERS.has(value.trim().toLowerCase());

export default function EmployerInlineAsk({
  onEmployerAdded,
}: {
  onEmployerAdded?: (name: string) => void;
}) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [hint, setHint] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Known employers with benefits on file, offered as native autocomplete so
  // members can tap instead of type. Fails silently when RLS blocks the read —
  // suggestions are a nicety, never a blocker.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from("employer_benefits")
          .select("employer_name")
          .eq("is_active", true)
          .limit(50);
        const names = Array.from(
          new Set(((data as any[]) || []).map((r) => r.employer_name).filter(Boolean)),
        ) as string[];
        setSuggestions(names.slice(0, 25));
      } catch {
        /* suggestions are optional */
      }
    })();
  }, []);

  const submit = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) return;
    if (isJunkEmployer(trimmed)) {
      setHint("That doesn't look like an employer. If you're not currently employed, tap \"Not employed right now\" below.");
      return;
    }
    setHint("");
    setSaving(true);
    try {
      await upsertUserAttributes({ employer_name: trimmed });
      dgTrack("behavioral", "jit_employer_added", { source: "grant-stack-results" });
      setDone(true);
      toast({
        title: "Employer saved",
        description: onEmployerAdded
          ? "Re-checking your Stack for employer-funded benefits…"
          : "We'll include employer benefits on your next Stack refresh.",
      });
      onEmployerAdded?.(trimmed);
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    dgTrack("behavioral", "jit_employer_skipped", { source: "grant-stack-results" });
    setDone(true);
  };

  if (done) return null;

  return (
    <Card className="p-4 sm:p-5 border-dashed bg-muted/20">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-full bg-purple-500/10 flex items-center justify-center shrink-0">
          <Briefcase className="h-4 w-4 text-purple-600" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-sm text-foreground flex items-center gap-1.5">
            Unlock the 4th layer of your Stack
            <Sparkles className="h-3.5 w-3.5 text-purple-600" />
          </p>
          <p className="text-xs text-muted-foreground">
            Add your employer and we'll check for down-payment matches, closing-cost help, or first-time buyer benefits offered where you work.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <Input
            placeholder="e.g. Boston Children's Hospital"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (hint) setHint("");
            }}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            list="employer-suggestions"
            autoComplete="organization"
            className="w-full"
          />
          <datalist id="employer-suggestions">
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
        <Button onClick={submit} disabled={saving || name.trim().length < 2} size="sm">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Check for employer benefits
        </Button>
      </div>
      {hint && <p className="mt-2 text-xs text-amber-700 dark:text-amber-500">{hint}</p>}
      <button
        type="button"
        onClick={skip}
        className="mt-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        Not employed right now
      </button>
    </Card>
  );
}
